import { showError } from '@calendly/alert'
import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { nanoid } from 'nanoid'
import { useDispatch } from 'react-redux'

import { useEventTypeContext } from 'features/event_type_provider'
import { useETEditorFormikPoolingType } from 'services/event_types'

import { getDefaultUserPoolName, getNextUserPoolName } from '../helpers'
import { MAX_ROUND_ROBIN_USER_POOLS } from '../utils'
import { useUserPoolsData } from './use_user_pools_data'

export const useCreateUserPool = () => {
  const dispatch = useDispatch()
  const { isCreation } = useEventTypeContext()

  const { values: {
    memberships,
    allocationPreference,
    pooling_type: poolingType,
  }, setValues } = useFormContext()

  const { userPools, roundRobinUserPools } = useUserPoolsData()
  const { isCollective, isRoundRobin, isCollectiveRoundRobin } = useETEditorFormikPoolingType()

  return () => {
    // When Collective Round Robin
    // Then we do not allow creating more than MAX_ROUND_ROBIN_USER_POOLS UserPools
    if (isCollectiveRoundRobin && roundRobinUserPools.length === MAX_ROUND_ROBIN_USER_POOLS) {
      dispatch(showError(I18n.t('publisher.event_types.members.validation.max_pools_reached')))

      return {
        isCreated: false,
        newUserPool: null
      }
    }

    // When Collective Round Robin
    // Then we add another Round Robin pool
    if (isCollectiveRoundRobin) {
      const newUserPool = {
        key: nanoid(5),
        allocation_preference: 'availability',
        pooling_type: 'round_robin',
        name: getNextUserPoolName(roundRobinUserPools),
        position: userPools.length
      }

      setValues((values) => ({
        ...values,
        allocationPreference: null,
        userPools: [
          ...userPools,
          newUserPool
        ]
      }))

      return {
        isCreated: true,
        newUserPool
      }
    }

    // When Collective without members
    // Then we convert it to Round Robin
    if (isCreation && isCollective && memberships.length === 0) {
      setValues((values) => ({
        ...values,
        allocationPreference: allocationPreference || 'availability',
        pooling_type: 'round_robin'
      }))

      return {
        isCreated: true,
        newUserPool: null
      }
    }

    // When Collective or Round Robin,
    // Then we convert it to Collective Round Robin
    // And add a pool
    if (isCollective || isRoundRobin) {
      const oldUserPoolKey = nanoid(5)
      const newUserPoolKey = nanoid(5)

      const newUserPool = {
        key: newUserPoolKey,
        allocation_preference: 'availability',
        pooling_type: 'round_robin',
        name: getDefaultUserPoolName(isRoundRobin ? 1 : 0),
        position: 1
      }

      setValues((values) => ({
        ...values,
        allocationPreference: null,
        pooling_type: 'multi_pool',
        memberships: memberships.map((membership) => ({ ...membership, user_pool_uid: oldUserPoolKey })),
        userPools: [
          {
            key: oldUserPoolKey,
            allocation_preference: allocationPreference || 'availability',
            pooling_type: poolingType,
            name: isRoundRobin ? getDefaultUserPoolName() : null,
            position: 0,
          },
          newUserPool
        ]
      }))

      return {
        isCreated: true,
        newUserPool
      }
    }

    return {
      isCreated: false,
      newUserPool: null
    }
  }
}
